/**
 * §P.6 fase 2 · El chat, el muro y el horario de silencio.
 *
 * ## El chat es **solo texto, y nunca con un menor**
 *
 * Las dos reglas viven en el esquema, no en la interfaz: `messages` no tiene columna
 * de adjunto y `conversation_participants` no admite a un alumno menor. El contrato
 * explica por qué la primera no es una limitación pendiente: «un canal por el que un
 * adulto puede mandarle una imagen a la familia de un menor es justo el riesgo que no
 * queremos, y **sin columna no se puede añadir por descuido**».
 *
 * Y por eso `adult_student` es un valor aparte en lugar de mezclarse con `guardian`:
 * un alumno mayor de edad habla por sí mismo; de un menor habla su tutor.
 *
 * ## `student_id` es de **quién se habla**, no quién habla
 *
 * La conversación es con el tutor. Saber de qué alumno va es lo que evita el «¿de
 * cuál de tus dos hijos me hablas?», así que se enseña siempre que lo haya.
 *
 * ## El horario de silencio **cruza la medianoche**
 *
 * 21:30 → 08:00 es el caso normal, no la excepción, y una comprobación ingenua
 * `from < hora < to` diría que a las 23:00 no hay silencio. Aquí se calcula bien
 * porque la pantalla enseña «ahora mismo estás en silencio», y decir lo contrario a
 * las once de la noche destruye la promesa entera.
 *
 * **No cancela avisos: los retrasa.** La fila se escribe igual con `sent_at` a nulo, y
 * eso también hay que decirlo: un maestro que crea que el aviso se perdió lo manda
 * otra vez por WhatsApp.
 */

export type ParteEnLaConversacion = 'guardian' | 'staff' | 'adult_student';

export const etiquetaDeParte = (parte: string): string =>
  ({
    guardian: 'Tutor',
    staff: 'Del club',
    adult_student: 'Alumno adulto',
  })[parte] ?? parte;

export interface ParticipanteEnConversacion {
  readonly user_id: string;
  readonly name: string;
  /** **Nunca un menor.** Ver la cabecera. */
  readonly party: ParteEnLaConversacion;
  readonly last_read_at: Date | string | null;
}

export interface Conversacion {
  readonly id: string;
  /** De **quién se habla**, no quién habla. */
  readonly student_id: string | null;
  readonly student_name: string | null;
  readonly subject: string | null;
  readonly created_by: string | null;
  readonly participants: readonly ParticipanteEnConversacion[];
  readonly last_message_at: Date | string | null;
  readonly last_message_preview: string | null;
  /** Sin leer **por quien pregunta**. */
  readonly unread: number;
  readonly closed_at: Date | string | null;
  readonly created_at: Date | string;
}

export interface Mensaje {
  readonly id: string;
  readonly conversation_id: string;
  readonly sender_user_id: string;
  readonly sender_name: string;
  readonly body: string;
  readonly sent_at: Date | string;
  readonly edited_at: Date | string | null;
  readonly deleted_at: Date | string | null;
}

/** Los tutores de la conversación, que es con quien se habla de verdad. */
export const tutoresDe = (c: Conversacion) =>
  c.participants.filter((p) => p.party !== 'staff');

/**
 * Cómo se llama una conversación en la lista.
 *
 * El asunto si lo hay; si no, de quién se habla; y si tampoco, con quién. Nunca un
 * hueco: una fila sin título en una bandeja es una fila que no se puede elegir.
 */
export function tituloDeConversacion(c: Conversacion): string {
  if (c.subject && c.subject.trim() !== '') return c.subject;
  if (c.student_name) return `Sobre ${c.student_name}`;

  const tutores = tutoresDe(c);
  if (tutores.length > 0) return tutores.map((p) => p.name).join(', ');
  return 'Conversación sin asunto';
}

export type VisibilidadDeEntrada = 'family' | 'group' | 'academy';

export const ALCANCES_DEL_MURO: Record<
  string,
  { etiqueta: string; explicacion: string }
> = {
  family: {
    etiqueta: 'Solo esa familia',
    explicacion: 'Lo ve la familia del alumno y nadie más.',
  },
  group: {
    etiqueta: 'Su grupo',
    explicacion: 'Lo ven las familias de ese grupo.',
  },
  academy: {
    etiqueta: 'Todo el club',
    explicacion: 'Lo ven todas las familias del club.',
  },
};

export interface EntradaDelMuro {
  readonly id: string;
  readonly student_id: string | null;
  readonly student_name: string | null;
  readonly group_id: string | null;
  readonly group_name: string | null;
  readonly type: string;
  /**
   * `system` es lo que se generó solo; `manual`, lo que alguien escribió.
   *
   * «La distinción es la que permite ver, mirando los datos, un club que **usa** el
   * producto frente a uno que escribe posts».
   */
  readonly source: 'system' | 'manual';
  readonly title: string | null;
  readonly body: string | null;
  readonly external_url: string | null;
  readonly visibility: VisibilidadDeEntrada;
  readonly occurred_at: Date | string;
  readonly created_by: string | null;
  readonly created_by_name: string | null;
}

/**
 * Los tipos de entrada del muro, en español.
 *
 * ## Salen de `CANALES`, y esta vez lo comprobé
 *
 * Escribí esta tabla de memoria y acerté **tres de siete**: `stripe.awarded`,
 * `birthday`, `exam.passed` y `announcement` no existen — los reales son
 * `student.birthday`, `exam.graded` y `announcement.published`, y las franjas no
 * llegan al muro—. Es la tercera tabla de enums que me invento en este proyecto
 * (antes: las acciones del registro de accesos y los estados de devolución), así que
 * queda dicho por tercera vez: **los enums salen del esquema o del código.**
 *
 * Solo están los que tienen `wall: true` en `CANALES`, que son los que de verdad
 * caen aquí, más `post` —lo que escribe una persona a mano—. Uno que no esté sale con
 * su código crudo: en un muro es feo, pero honesto.
 */
const TIPOS: Record<string, string> = {
  // Lo que escribe una persona: `source: 'manual'`.
  post: 'Publicación del club',
  // Lo que se genera solo, en el orden en que se ve un curso.
  'attendance.recorded': 'Lista pasada',
  'checkin.entered': 'Entró a clase',
  'checkin.exited': 'Salió de clase',
  'checkin.picked_up': 'Le recogieron',
  'rank.awarded': 'Grado nuevo',
  'exam.graded': 'Examen calificado',
  'exam.countdown': 'Cuenta atrás para el examen',
  'session.cancelled': 'Clase cancelada',
  'student.birthday': 'Cumpleaños',
  'student.anniversary': 'Aniversario en el club',
  'attendance.streak': 'Racha de asistencia',
  'season.summary': 'Resumen de la temporada',
  'competition.called_up': 'Convocado a competición',
  'competition.result': 'Resultado de competición',
  'invoice.paid': 'Recibo pagado',
};

export const etiquetaDeTipoDeEntrada = (tipo: string): string =>
  TIPOS[tipo] ?? tipo;

export interface HorarioDeSilencio {
  /** `HH:MM`. */
  readonly from: string;
  readonly to: string;
  readonly timezone: string;
  /** Si el club no lo ha fijado y son los valores de fábrica. */
  readonly is_default: boolean;
}

/**
 * Si una hora cae dentro de la ventana de silencio.
 *
 * **La ventana cruza la medianoche en el caso normal** —21:30 → 08:00— y una
 * comprobación ingenua `from <= h && h < to` diría que a las 23:00 no hay silencio.
 * Es el caso corriente, no la excepción, así que se resuelve aquí y con una prueba.
 */
export function enSilencio(
  horario: { from: string; to: string },
  hora: string
): boolean {
  const { from, to } = horario;
  // Mismo día: 13:00 → 15:00. La ventana es un tramo normal.
  if (from <= to) return hora >= from && hora < to;
  // Cruza la medianoche: se está en silencio antes de `to` o después de `from`.
  return hora >= from || hora < to;
}

/** `HH:MM` de ahora en la zona que diga el club, no en la del navegador. */
export function horaEnZona(zona: string, ahora = new Date()): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      timeZone: zona,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(ahora);
  } catch {
    /*
     * Una zona que el navegador no conoce no puede dejar la pantalla en blanco: se
     * cae a la hora local, que es una aproximación honesta y no un error.
     */
    return new Intl.DateTimeFormat('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(ahora);
  }
}

/**
 * Cuánto dura la ventana, en palabras.
 *
 * Sirve para el caso que cuesta ver: alguien pone 08:00 → 21:30 pensando que define
 * las horas de trabajo, y acaba de poner **trece horas y media de silencio en mitad
 * del día**. Diciendo la duración se descubre antes de guardar.
 */
export function duracionDelSilencio(from: string, to: string): number {
  const min = (h: string) => {
    const [hh, mm] = h.split(':').map(Number);
    return (hh ?? 0) * 60 + (mm ?? 0);
  };
  const d = min(to) - min(from);
  return d > 0 ? d : d + 24 * 60;
}

export function duracionEnPalabras(minutos: number): string {
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  if (h === 0) return `${m} minutos`;
  if (m === 0) return h === 1 ? 'una hora' : `${h} horas`;
  return `${h} h ${m} min`;
}
